import React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import {
  Type,
  MousePointer2,
  Contrast,
  Zap,
  BookOpen,
  Focus,
  X,
  RotateCcw,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { clsx } from 'clsx'
import { A11yControlButton } from './A11yControlButton'
import { CONTROL_CONFIG, DEFAULT_PREFS } from '../constants'
import { useA11yStore } from '../store'
import type { A11yPrefs } from '../types'

const ICON_MAP: Record<string, LucideIcon> = {
  Type,
  MousePointer2,
  Contrast,
  Zap,
  BookOpen,
  Focus,
}

interface A11ySheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function A11ySheet({
  open,
  onOpenChange,
}: A11ySheetProps): React.ReactElement {
  const prefs = useA11yStore()
  const cycleControl = useA11yStore((state) => state.cycleControl)
  const resetAll = useA11yStore((state) => state.resetAll)

  const handleCycleControl = (key: keyof A11yPrefs) => {
    cycleControl(key)
  }

  const handleResetAll = () => {
    resetAll()
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          className={clsx(
            'fixed inset-y-0 right-0 z-[70] w-80',
            'bg-white dark:bg-slate-900',
            'flex flex-col'
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-sand-300 dark:border-slate-700 px-6 py-4">
            <Dialog.Title className="text-lg font-semibold text-slate-900 dark:text-white">
              Accessibility
            </Dialog.Title>
            <Dialog.Close
              className={clsx(
                'rounded-md p-1 transition-colors',
                'hover:bg-sand-200 dark:hover:bg-slate-800',
                'text-slate-600 dark:text-sand-400'
              )}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>

          {/* Controls Grid */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <div className="grid grid-cols-2 gap-4">
              {CONTROL_CONFIG.map((control) => {
                const Icon =
                  ICON_MAP[control.iconName] ||
                  ICON_MAP.Type

                const currentValue = prefs[control.key] as string
                const isActive = currentValue !== (DEFAULT_PREFS[control.key] as string)

                return (
                  <A11yControlButton
                    key={control.key}
                    controlKey={control.key}
                    label={control.label}
                    Icon={Icon}
                    currentState={currentValue}
                    onClick={() =>
                      handleCycleControl(control.key)
                    }
                    isActive={isActive}
                  />
                )
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-sand-300 dark:border-slate-700 px-6 py-4">
            <button
              onClick={handleResetAll}
              className={clsx(
                'w-full flex items-center justify-center gap-2',
                'px-4 py-2 rounded-lg text-sm font-medium',
                'bg-sand-200 dark:bg-slate-800',
                'text-slate-700 dark:text-sand-300',
                'hover:bg-sand-300 dark:hover:bg-slate-700',
                'transition-colors'
              )}
              type="button"
            >
              <RotateCcw className="h-4 w-4" />
              Reset all
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
